import { useCallback, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  MatchAutoMatchDocument,
  RetryMatchAutoRunDocument,
  SetMatchAutoMatchDocument,
} from "@/graphql/generated/operations";
import type { LinkedListing } from "../LinkedListings";
import { isInFlight, type AutoRun } from "./auto-match-runs";

// An enabled match must discover its first new run too, even when no run was pending at mount.
const IN_FLIGHT_POLL_MS = 20_000;

const NO_RUNS: readonly AutoRun[] = [];
const NO_LISTINGS: readonly LinkedListing[] = [];

/**
 * Schalter und Läufe der automatischen Bewertung eines Matches. Einmal geladen; `poll` lädt sanft nach,
 * solange ein Lauf unterwegs ist, und nach "Erneut versuchen" wird neu geladen.
 */
export function useMatchAutoMatch(
  matchId: string,
  { poll = false, skip = false }: { poll?: boolean; skip?: boolean } = {},
) {
  const [watching, setWatching] = useState(false);
  const query = useQuery(MatchAutoMatchDocument, {
    variables: { id: matchId },
    skip,
    // Wie MatchDetail: ein Teilfehler soll nicht alles verwerfen, was genesis geliefert hat.
    errorPolicy: "all",
    fetchPolicy: "cache-and-network",
    // Deklarativ, 0 heißt angehalten. Der Zustand darüber folgt den geladenen Läufen.
    pollInterval: poll && watching ? IN_FLIGHT_POLL_MS : 0,
  });
  const match = query.data?.match ?? null;
  const runs = match?.autoMatchRuns ?? NO_RUNS;
  const listings = match?.listings ?? NO_LISTINGS;
  const shouldWatch = match?.autoMatch != null || runs.some(isInFlight);
  if (shouldWatch !== watching) setWatching(shouldWatch);

  const [setAutoMatch] = useMutation(SetMatchAutoMatchDocument);
  const [retryRun] = useMutation(RetryMatchAutoRunDocument);
  const { refetch } = query;

  const setEnabled = useCallback(
    async (enabled: boolean) => {
      await setAutoMatch({ variables: { input: { matchId, enabled } } });
    },
    [matchId, setAutoMatch],
  );

  const retry = useCallback(
    async (runId: string) => {
      await retryRun({ variables: { input: { runId } } });
      await refetch();
    },
    [refetch, retryRun],
  );

  return {
    loading: query.loading && !match,
    error: match ? null : (query.error ?? null),
    autoMatch: match?.autoMatch ?? null,
    canSet: match?.actions.canSetAutoMatch === true,
    runs,
    /** Die Ausschreibungen, für die der Schalter wirkt, soweit genesis sie der Person nennt. */
    listings,
    /**
     * Ob Schalter und Läufe hierher gehören: das Match hängt an einer Ausschreibung, die die Person sieht,
     * ist bereits eingeschaltet oder hat schon Läufe. Dieselben Serverfakten gelten für Personen und
     * Unternehmen; Aufrufer filtern weder nach Match-Modus noch nach sichtbaren Ausschreibungen.
     */
    relevant: listings.length > 0 || runs.length > 0 || match?.autoMatch != null,
    setEnabled,
    retry,
  };
}
